import React, { useState, useCallback } from 'react';

const validators = {
  name: v => {
    if (!v.trim()) return 'Full name is required';
    if (v.trim().length < 2) return 'Name must be at least 2 characters';
    if (!/^[a-zA-Z\s'-]+$/.test(v.trim())) return 'Name can only contain letters';
    return '';
  },
  email: v => {
    if (!v.trim()) return 'Email address is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) return 'Please enter a valid email address';
    return '';
  },
  phone: v => {
    if (!v.trim()) return 'Phone number is required';
    const digits = v.replace(/\D/g, '');
    if (digits.length < 10) return 'Please enter a valid UK phone number';
    return '';
  },
  service: v => (!v ? 'Please select a service' : ''),
  message: v => {
    if (!v.trim()) return 'Please tell us a bit about your needs';
    if (v.trim().length < 10) return 'Message is too short — add a few more details';
    return '';
  },
};

function ValidatedField({ label, required, error, touched, children }) {
  const showError = touched && error;
  const showSuccess = touched && !error;
  return (
    <div style={{ marginBottom: 18, flex: 1, minWidth: 0 }}>
      <label style={{
        display: 'flex', alignItems: 'center', gap: 4,
        fontSize: '0.75rem', fontWeight: 600,
        color: showError ? '#fca5a5' : 'rgba(255,255,255,0.7)',
        textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 7,
        transition: 'color 0.2s',
      }}>
        {label}
        {required && <span style={{ color: '#f58c1e' }}>*</span>}
      </label>
      <div style={{ position: 'relative' }}>
        {children}
        {touched && (
          <div style={{
            position: 'absolute', right: 12, top: '50%',
            transform: 'translateY(-50%)', fontSize: 14,
            color: showError ? '#f87171' : '#4ade80',
            pointerEvents: 'none',
          }}>
            {showError ? '✕' : '✓'}
          </div>
        )}
      </div>
      <div style={{ maxHeight: showError ? '50px' : '0', overflow: 'hidden', transition: 'max-height 0.25s ease' }}>
        <p style={{ margin: '5px 0 0', fontSize: '0.74rem', color: '#fca5a5' }}>⚠ {error}</p>
      </div>
    </div>
  );
}

function iStyle(touched, error, extra = {}) {
  const showError = touched && error;
  const showSuccess = touched && !error;
  return {
    width: '100%',
    background: showError ? 'rgba(239,68,68,0.1)' : showSuccess ? 'rgba(74,222,128,0.07)' : 'rgba(255,255,255,0.08)',
    border: `1.5px solid ${showError ? 'rgba(239,68,68,0.6)' : showSuccess ? 'rgba(74,222,128,0.5)' : 'rgba(255,255,255,0.15)'}`,
    borderRadius: 10, padding: '12px 40px 12px 16px',
    color: '#fff', fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.92rem', outline: 'none',
    transition: 'border-color 0.2s, background 0.2s',
    ...extra,
  };
}

function FormProgress({ filled, total }) {
  const pct = Math.round((filled / total) * 100);
  const color = pct < 40 ? '#f58c1e' : pct < 80 ? '#facc15' : '#4ade80';
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)' }}>Form progress</span>
        <span style={{ fontSize: '0.72rem', color, fontWeight: 600 }}>{pct}%</span>
      </div>
      <div style={{ height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.4s ease, background 0.4s ease' }} />
      </div>
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState('idle');

  const REQUIRED = ['name', 'email', 'phone', 'service', 'message'];
  const errors = Object.fromEntries(Object.keys(validators).map(k => [k, validators[k](form[k] || '')]));
  const isValid = REQUIRED.every(k => !errors[k]);
  const filledCount = REQUIRED.filter(k => form[k] && !errors[k]).length;
  const anyTouched = Object.values(touched).some(Boolean);

  const set = useCallback(k => e => setForm(f => ({ ...f, [k]: e.target.value })), []);
  const blur = useCallback(k => () => setTouched(t => ({ ...t, [k]: true })), []);

 const handleSubmit = async () => {
  setTouched(Object.fromEntries(REQUIRED.map(k => [k, true])));
  if (!isValid) return;

  setStatus('sending');

  try {
    const res = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        access_key: "597930d8-02a3-487d-be7c-f3ca80cd3c39",
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        message: form.message
      })
    });

    const data = await res.json();

    if (!data.success) throw new Error();

    setStatus('sent');

    setTimeout(() => {
      setStatus('idle');
      setForm({ name: '', email: '', phone: '', service: '', message: '' });
      setTouched({});
    }, 3000);

  } catch {
    setStatus('idle');
    alert('Something went wrong. Please try again.');
  }
};

  const btnBg = status === 'sent' ? '#16a34a' : (!isValid && anyTouched) ? 'rgba(245,140,30,0.5)' : 'var(--orange)';
  const btnText = status === 'idle' ? (isValid ? 'Send Message →' : 'Complete the form to send')
    : status === 'sending' ? 'Sending…' : '✓ Message Sent!';

  const contactItems = [
    { icon: '/icon/call.png',    alt: 'phone',    title: 'Call or WhatsApp Us', val: <a href="tel:+447853481188" style={{ color: 'var(--orange)', textDecoration: 'none' }}>07853 481188</a> },
    { icon: '/icon/gmail.png',    alt: 'email',    title: 'Email Us',            val: <a href="mailto:se1hostcarecleaning@gmail.com" style={{ color: 'var(--orange)', textDecoration: 'none' }}>se1hostcarecleaning@gmail.com</a> },
    { icon: '/icon/placeholder.png', alt: 'location', title: 'Service Area',        val: 'SE1 – Waterloo, Southwark, London Bridge, Elephant & Castle' },
  ];

  return (
    <section id="contact" style={{ padding: '100px 5%', background: 'linear-gradient(135deg, var(--navy) 0%, var(--blue) 100%)', display: 'flex', gap: 80, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <style>{`
        @media (max-width: 768px) { #contact { flex-direction: column !important; } }
        .ci:focus { border-color: var(--orange) !important; background: rgba(255,255,255,0.12) !important; }
        .ci::placeholder { color: rgba(255,255,255,0.3); }
        .ci option { background: #0f2340; color: #fff; }
      `}</style>

      {/* LEFT: Info */}
      <div style={{ flex: 1, minWidth: 260 }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, letterSpacing: '2.5px', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: 12 }}>Get In Touch</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.7rem, 3.5vw, 2.4rem)', color: '#fff', lineHeight: 1.25, marginBottom: 18 }}>
          Ready for a Spotless Space?
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: 36 }}>
          Contact us today for a free, no-obligation quote. We're here to help with all your cleaning needs across SE1.
        </p>
        {contactItems.map(({ icon, alt, title, val }) => (
          <div key={title} style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
            <div style={{ width: 48, height: 48, minWidth: 48, background: 'rgba(255,255,255,0.1)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={icon} alt={alt} style={{ width: 24, height: 24, objectFit: 'contain' }} />
            </div>
            <div>
              <strong style={{ display: 'block', color: '#fff', fontSize: '0.88rem', marginBottom: 2 }}>{title}</strong>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>{val}</span>
            </div>
          </div>
        ))}
      </div>

      {/* RIGHT: Form with validation */}
      <div style={{ flex: 1, minWidth: 300 }}>
        <div style={{ background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: 24, padding: '40px 36px', backdropFilter: 'blur(10px)' }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#fff', fontSize: '1.35rem', marginBottom: 6 }}>Send Us a Message</h3>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', marginBottom: 24 }}>Fields marked <span style={{ color: '#f58c1e' }}>*</span> are required</p>

          <FormProgress filled={filledCount} total={REQUIRED.length} />

          <div style={{ display: 'flex', gap: 16 }}>
            <ValidatedField label="Your Name" required error={errors.name} touched={touched.name}>
              <input className="ci" value={form.name} onChange={set('name')} onBlur={blur('name')} placeholder="Jane Smith" style={iStyle(touched.name, errors.name)} />
            </ValidatedField>
            <ValidatedField label="Email" required error={errors.email} touched={touched.email}>
              <input className="ci" type="email" value={form.email} onChange={set('email')} onBlur={blur('email')} placeholder="jane@email.com" style={iStyle(touched.email, errors.email)} />
            </ValidatedField>
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <ValidatedField label="Phone" required error={errors.phone} touched={touched.phone}>
              <input className="ci" type="tel" value={form.phone} onChange={set('phone')} onBlur={blur('phone')} placeholder="07700 000000" style={iStyle(touched.phone, errors.phone)} />
            </ValidatedField>
            <ValidatedField label="Service" required error={errors.service} touched={touched.service}>
              <select className="ci" value={form.service} onChange={set('service')} onBlur={blur('service')} style={iStyle(touched.service, errors.service, { padding: '12px 16px' })}>
                <option value="">Select service…</option>
                <option>Airbnb / Short Let Cleaning</option>
                <option>End of Tenancy Cleaning</option>
                <option>Residential Cleaning</option>
                <option>One Off Deep Clean</option>
              </select>
            </ValidatedField>
          </div>

          <ValidatedField label="Message" required error={errors.message} touched={touched.message}>
            <textarea className="ci" value={form.message} onChange={set('message')} onBlur={blur('message')} placeholder="Tell us about your property and when you need us…" style={iStyle(touched.message, errors.message, { padding: '12px 16px', height: 110, resize: 'vertical' })} />
            <div style={{ textAlign: 'right', fontSize: '0.7rem', marginTop: 4, color: form.message.length > 10 ? 'rgba(74,222,128,0.6)' : 'rgba(255,255,255,0.25)' }}>
              {form.message.length} chars
            </div>
          </ValidatedField>

          <button onClick={handleSubmit} disabled={status !== 'idle'} style={{
            width: '100%', border: 'none', borderRadius: 10, padding: 14,
            fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 600,
            cursor: status !== 'idle' ? 'default' : 'pointer', marginTop: 4,
            background: btnBg, color: '#fff',
            opacity: status === 'sending' ? 0.7 : 1,
            transform: status === 'idle' ? 'none' : 'scale(0.99)',
            boxShadow: status === 'sent' ? '0 4px 16px rgba(22,163,74,0.35)' : '0 4px 16px rgba(245,140,30,0.3)',
            transition: 'background 0.3s, opacity 0.2s, box-shadow 0.3s, transform 0.15s',
          }}>
            {btnText}
          </button>

          {!isValid && anyTouched && (
            <div style={{ marginTop: 14, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 16px' }}>
              <p style={{ color: '#fca5a5', fontSize: '0.8rem', fontWeight: 600, marginBottom: 6 }}>Please fix the following:</p>
              <ul style={{ paddingLeft: 16, margin: 0 }}>
                {REQUIRED.filter(k => errors[k]).map(k => (
                  <li key={k} style={{ color: '#fca5a5', fontSize: '0.76rem', marginBottom: 3 }}>{errors[k]}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}